/**
 * Orders Zustand store.
 * Hand-rolled (not using the resource factory) because orders has multiple
 * list endpoints (admin, customer-self, sales-rep-self) and a few non-CRUD
 * actions (cancel, status history).
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  Order,
  OrderSummary,
  OrderStatusHistoryEntry,
  ListOrdersFilters,
  CreateOrderInput,
  listOrders,
  listMyCustomerOrders,
  listMySalesRepOrders,
  getOrder,
  getOrderStatusHistory,
  getOrdersSummary,
  createOrder,
  cancelOrder,
} from '../services/ordersApi';

export type OrdersListSource = 'admin' | 'customer-self' | 'sales-rep-self';

interface OrdersState {
  // List
  items: Order[];
  total: number;
  loading: boolean;
  saving: boolean;
  error: string | null;

  // Detail
  current: Order | null;
  history: OrderStatusHistoryEntry[];

  // Summary
  summary: OrderSummary | null;
  summaryLoading: boolean;

  // Filters
  filters: ListOrdersFilters;
  source: OrdersListSource;

  setSource: (source: OrdersListSource) => void;
  setFilters: (patch: Partial<ListOrdersFilters>) => void;
  resetFilters: () => void;

  fetchList: () => Promise<void>;
  fetchOne: (id: number | string) => Promise<Order>;
  fetchHistory: (id: number | string) => Promise<void>;
  fetchSummary: () => Promise<void>;
  create: (input: CreateOrderInput) => Promise<Order>;
  cancel: (id: number | string) => Promise<void>;
  clearCurrent: () => void;
  reset: () => void;
}

const defaultFilters: ListOrdersFilters = { limit: 20, offset: 0, sort_by: 'created_at' };

const initial = {
  items: [] as Order[],
  total: 0,
  loading: false,
  saving: false,
  error: null as string | null,
  current: null as Order | null,
  history: [] as OrderStatusHistoryEntry[],
  summary: null as OrderSummary | null,
  summaryLoading: false,
  filters: { ...defaultFilters },
  source: 'admin' as OrdersListSource,
};

function toErr(e: unknown): string {
  return e instanceof Error ? e.message : 'Something went wrong';
}

export const useOrdersStore = create<OrdersState>()(
  devtools(
    (set, get) => ({
      ...initial,

      setSource: (source) => set({ source }),
      setFilters: (patch) => set({ filters: { ...get().filters, ...patch, offset: 0 } }),
      resetFilters: () => set({ filters: { ...defaultFilters } }),

      fetchList: async () => {
        set({ loading: true, error: null });
        try {
          const fn =
            get().source === 'customer-self'
              ? listMyCustomerOrders
              : get().source === 'sales-rep-self'
                ? listMySalesRepOrders
                : listOrders;
          const { data, total } = await fn(get().filters);
          set({ items: data, total, loading: false });
        } catch (err) {
          set({ loading: false, error: toErr(err) });
        }
      },

      fetchOne: async (id) => {
        set({ loading: true, error: null });
        try {
          const order = await getOrder(id);
          set({ current: order, loading: false });
          return order;
        } catch (err) {
          set({ loading: false, error: toErr(err) });
          throw err;
        }
      },

      fetchHistory: async (id) => {
        try {
          const result = await getOrderStatusHistory(id);
          set({ history: result.status_history });
        } catch (err) {
          set({ error: toErr(err) });
        }
      },

      fetchSummary: async () => {
        set({ summaryLoading: true });
        try {
          const summary = await getOrdersSummary();
          set({ summary, summaryLoading: false });
        } catch (err) {
          set({ summaryLoading: false, error: toErr(err) });
        }
      },

      create: async (input) => {
        set({ saving: true, error: null });
        try {
          const created = await createOrder(input);
          set((s) => ({ items: [created, ...s.items], saving: false }));
          return created;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },

      cancel: async (id) => {
        set({ saving: true, error: null });
        try {
          await cancelOrder(id);
          set((s) => ({
            items: s.items.map((o) =>
              String(o.id) === String(id) ? { ...o, status: 'cancelled' } : o
            ),
            current:
              s.current && String(s.current.id) === String(id)
                ? { ...s.current, status: 'cancelled' }
                : s.current,
            saving: false,
          }));
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },

      clearCurrent: () => set({ current: null, history: [] }),

      reset: () => set({ ...initial, filters: { ...defaultFilters } }),
    }),
    { name: 'orders-store' }
  )
);
